import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Ticket, Lock, CheckCircle, Users, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyToursPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [adminTours, setAdminTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { token, user } = useAuth();

  useEffect(() => {
    if (!token || !user) return;
    
    const loadTours = async () => {
      setLoading(true);
      
      const headers = { Authorization: `Bearer ${token}` };
      const isAdminUser = user.account_type === 'admin';
      
      // Run both requests in parallel - neither blocks the other
      const requests = [
        axios.get(`${API}/purchases/my-tours`, { headers }).catch(e => {
          console.error('Failed to load purchases:', e.response?.status, e.response?.data);
          return { data: [] };
        }),
      ];
      if (isAdminUser) {
        requests.push(
          axios.get(`${API}/admin/tours`, { headers }).catch(e => {
            console.error('Failed to load admin tours:', e.response?.status, e.response?.data);
            return { data: [] };
          })
        );
      }
      
      const results = await Promise.all(requests);
      setPurchases(results[0].data || []);
      if (isAdminUser && results[1]) {
        setAdminTours(results[1].data || []);
      }
      
      setLoading(false);
    };
    loadTours();
  }, [token, user]);

  const handleAdminTestTour = (tour) => {
    navigate(`/tour-player/admin-test-${tour.tour_id}?mode=test`);
  };

  const handleAdminPlayTour = (tour) => {
    navigate(`/tour-player/admin-test-${tour.tour_id}?mode=regular`);
  };

  const handleTourClick = (purchase) => {
    if (purchase.active) {
      navigate(`/tour-player/${purchase.purchase_id}`);
    } else {
      navigate(`/tours/${purchase.tour_id}`);
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  // Show spinner until user is loaded and data is fetched
  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  const isAdmin = user.account_type === 'admin';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-4">
            <Ticket className="w-10 h-10 text-teal-500 mr-3" />
            <h1 className="text-4xl font-bold font-sans">My Tours</h1>
          </div>
          <p className="text-slate-300 text-lg font-body">
            {isAdmin ? 'All tours (Admin access)' : 'Your purchased audio tours'}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* ADMIN VIEW */}
        {isAdmin ? (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <FlaskConical className="w-8 h-8 text-purple-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900" data-testid="admin-tours-heading">All Tours</h2>
                <p className="text-gray-500 text-sm">Test any tour without purchasing</p>
              </div>
            </div>
            
            {adminTours.length === 0 ? (
              <div className="text-center py-16">
                <FlaskConical className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-4">No tours created yet</p>
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full font-semibold"
                >
                  Go to Admin Dashboard
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {adminTours.map((tour) => (
                  <div
                    key={tour.tour_id}
                    className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6 hover:border-purple-400 transition-all"
                    data-testid={`admin-tour-card-${tour.tour_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{tour.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">
                          {tour.city} &bull; {tour.stop_count || 0} stops {tour.duration ? `\u2022 ${tour.duration}` : ''}
                        </p>
                        {!tour.published && (
                          <span className="inline-block bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full">
                            Unpublished
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleAdminPlayTour(tour)}
                          className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 text-sm"
                          data-testid={`play-tour-${tour.tour_id}`}
                        >
                          <Ticket className="w-4 h-4" />
                          Play Tour
                        </button>
                        <button
                          onClick={() => handleAdminTestTour(tour)}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full font-bold flex items-center gap-2 text-sm"
                          data-testid={`test-tour-${tour.tour_id}`}
                        >
                          <FlaskConical className="w-4 h-4" />
                          Test Tour
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* REGULAR USER VIEW */
          <div>
            {purchases.length === 0 ? (
              <div className="text-center py-16">
                <Lock className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-4 font-body">You haven't purchased any tours yet</p>
                <button
                  onClick={() => navigate('/')}
                  data-testid="browse-tours-button"
                  className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
                >
                  Browse Tours
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {purchases.map((purchase) => (
                  <div
                    key={purchase.purchase_id}
                    onClick={() => handleTourClick(purchase)}
                    data-testid={`purchase-card-${purchase.purchase_id}`}
                    className="bg-white rounded-2xl shadow-card hover:shadow-floating transition-all cursor-pointer overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-slate-900 mb-1 font-sans">
                            {purchase.tour?.title || 'Tour'}
                          </h3>
                          <p className="text-teal-600 font-body">{purchase.tour?.city || ''}</p>
                        </div>
                        <div>
                          {purchase.active ? (
                            <span className="flex items-center px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold font-body">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-semibold font-body">
                              <Lock className="w-4 h-4 mr-2" />
                              Expired
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-slate-500 text-xs uppercase tracking-wider font-body">Purchased</span>
                          <p className="text-slate-900 font-semibold font-body">{formatDate(purchase.purchased_at)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs uppercase tracking-wider font-body">Expires</span>
                          <p className="text-slate-900 font-semibold font-body">{formatDate(purchase.expires_at)}</p>
                        </div>
                      </div>

                      {purchase.additional_devices > 0 && (
                        <div className="flex items-center text-cyan-600 mb-4">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          <span className="font-semibold font-body">{purchase.total_devices || (1 + purchase.additional_devices)} Devices Enabled</span>
                        </div>
                      )}

                      {!purchase.active && (
                        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                          <p className="text-orange-700 font-semibold mb-2 font-body">Expired - Repurchase to Unlock</p>
                          <p className="text-sm text-orange-600 font-body">This tour has expired. Purchase again to regain access.</p>
                        </div>
                      )}

                      <button
                        data-testid={`action-button-${purchase.purchase_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (purchase.active) {
                            if (purchase.additional_devices > 0) {
                              navigate(`/invite-guests?purchase=${purchase.purchase_id}`);
                            } else {
                              navigate(`/tour-player/${purchase.purchase_id}`);
                            }
                          }
                        }}
                        className={`w-full mt-4 py-3 rounded-full font-bold transition-all active:scale-95 font-sans flex items-center justify-center ${
                          purchase.active
                            ? 'bg-teal-500 hover:bg-teal-600 text-white'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                      >
                        {purchase.active ? (
                          purchase.additional_devices > 0 ? (
                            <>
                              <Users className="w-5 h-5 mr-2" />
                              Invite and Start Tour
                            </>
                          ) : (
                            'Start Tour'
                          )
                        ) : (
                          'Repurchase Tour'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyToursPage;
